import React, { useEffect, useState } from "react";
import { CalendarDays, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { trDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = ["Yıllık", "Ücretsiz", "Hastalık", "Mazeret"];
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function StaffLeaveDialog({ staff, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ type: "Yıllık", start_date: todayISO(), end_date: todayISO(), note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => staff && api.get(`/staff/${staff.id}/leaves`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staff]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/staff-leaves", { ...form, staff_id: staff.id });
      toast.success("İzin kaydedildi"); load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/staff-leaves/${id}`); toast.success("İzin silindi"); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!staff} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="leave-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> İzin Takibi — {staff?.name}</DialogTitle>
        </DialogHeader>
        {staff && (
          <div className="rounded-lg bg-muted/40 p-3 text-sm flex justify-between" data-testid="leave-summary">
            <span>{staff.leave_year} yıllık izin hakkı: <b className="font-mono">{staff.annual_leave_days ?? 14}</b> gün</span>
            <span>Kullanılan <b className="font-mono">{staff.leave_used}</b> · Kalan <b className={`font-mono ${staff.leave_remaining < 0 ? "text-rose-400" : "text-emerald-400"}`}>{staff.leave_remaining}</b></span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <F label="Tür">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Not"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="leave-note" /></F>
          <F label="Başlangıç"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" className="font-mono" /></F>
          <F label="Bitiş"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" className="font-mono" /></F>
        </div>
        <Button onClick={save} disabled={saving} data-testid="leave-save" className="gap-2 w-full">{saving && <Loader2 className="h-4 w-4 animate-spin" />} İzin Ekle</Button>
        <div className="max-h-56 overflow-y-auto divide-y divide-border/60 text-sm">
          {rows.length === 0 && <div className="text-xs text-muted-foreground py-3 text-center">İzin kaydı yok</div>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`leave-row-${r.id}`} className="flex items-center justify-between py-2">
              <div>
                <span className={`text-xs font-semibold ${r.type === "Yıllık" ? "text-primary" : "text-muted-foreground"}`}>{r.type}</span>
                <span className="ml-2">{trDate(r.start_date)} – {trDate(r.end_date)}</span>
                {r.note && <span className="text-muted-foreground text-xs"> · {r.note}</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{r.days} gün</span>
                <button onClick={() => del(r.id)} data-testid={`leave-delete-${r.id}`} className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
